import { useMemo, useState } from "react";
import DataTable from "../../../components/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faDownload } from "@fortawesome/free-solid-svg-icons";
import { Dropdown } from "primereact/dropdown";
import Button, { ButtonType } from "../../../components/Button";
import "./UsageReport.scss";
import useFetchAuto from "../../../hooks/useFetchAuto";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import useFetch from "../../../hooks/useFetch";
import { formatDateWithHyphen } from "../../../utils/formatting";
import { getFetchOptions } from "../../../utils/fetch";
import { saveAs } from "file-saver";
import { useSession, LogoutReason } from "../../Auth/hooks/useSession";
import AuthLogoutMessage from "../../Auth/components/AuthLogoutMessage";
import DatePicker from "./components/DatePicker";

const UsageReport = () => {
  const { reason } = useSession();

  const sum = (arr, key) => arr.reduce((acc, x) => acc + (x?.[key] || 0), 0);
  const flattenCompanies = (r) => r.Companies || [];
  const flattenUsers = (c) => c.Users || [];
  const getDefaultDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };
  const { ready, execute, executingFetch } = useFetch();

  // --- table/data state
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // --- ui state
  const [expandedResellers, setExpandedResellers] = useState(new Set());
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [hideZero, setHideZero] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const currentDate = getDefaultDate();
  const currentDateEndOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
  const [selectedRange, setSelectedRange] = useState({ from: currentDate, to: currentDateEndOfMonth });

  // --- packages
  const {
    loadingAvailablePackages,
    data: availablePackages,
    error: availPackagesError
  } = useFetchAuto("/api/Reports/Usage/availablePackages", null);

  // --- build dropdown options 
  const packageOptions = Array.isArray(availablePackages)
    ? availablePackages
      .filter((p) => p && typeof p.Id !== "undefined" && typeof p.Name === "string")
      .map((p) => ({ label: p.Name, value: p.Id }))
    : [];

  // --- utils
  const resetTableState = () => {
    setData(null);
    setDataError(null);
    setExpandedResellers(new Set());
    setExpandedCompanies(new Set());
  };

  const fetchReport = (pkgId, startDate, endDate) => {
    if (!ready) {
      return;
    }

    if (!pkgId && pkgId !== 0) {
      return;
    }

    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
      return;
    }

    resetTableState();
    setDataLoading(true);

    const body = {
      packageId: pkgId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };

    const options = getFetchOptions({
      method: "POST",
      body
    });

    execute("/api/Reports/Usage", options, (error, newData) => {
      setDataLoading(false);
      if (error) {
        setDataError(error.message || "Failed to load usage report.");
        return;
      }
      setData(newData || null);
    });
  };

  const companyHasLogins = (company) => {
    const users = flattenUsers(company) || [];
    return sum(users, "Logins") > 0;
  };

  const countAllUsers = (resellers) =>
    (resellers ?? [])
      .flatMap((r) => (r.Companies ?? []))
      .flatMap((c) => (c.Users ?? [])).length;

  // --- handlers
  const handlePackageChange = (pkgId) => {
    setSelectedPackage(pkgId);

    if (pkgId === null) {
      resetTableState();
      return;
    }

    // If a range is currently selected, use that
    if (selectedRange?.from && selectedRange?.to) {
      fetchReport(pkgId, selectedRange.from, selectedRange.to);
      return;
    }
  };

	const handleRangeChange = (range) => {
		setSelectedRange(range);

    if (selectedPackage === null) {
      resetTableState();
      return;
    }

    const { from, to } = range || {};

    if (from instanceof Date && to instanceof Date) {
      fetchReport(selectedPackage, from, to);
    } else {
      resetTableState();
    }
  };

  const handleMonthChange = (monthDate) => {
    if (!monthDate) {
      setSelectedRange({ from: null, to: null });
      resetTableState();
      return;
    }

    const from = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1, 0, 0, 0, 0);

    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);

    setSelectedRange({ from, to });

    if (selectedPackage != null) {
      fetchReport(selectedPackage, from, to);
    }
  };

  // --- rows
  const filteredResellers = useMemo(() => {
    return data?.Resellers ?? [];
  }, [data]);

  const baseResellerRows = useMemo(() => {
    return (filteredResellers || []).map((r, resellerId) => {
      const companies = flattenCompanies(r);
      const users = companies.flatMap((c) => flattenUsers(c));
      const resellerKey = typeof r.ResellerId !== "undefined" ? String(r.ResellerId) : String(resellerId);
      return {
        Id: `r-${resellerKey}`,
        Type: "reseller",
        Level: 0,
        Name: r.ResellerName,
        Logins: sum(users, "Logins"),
        Views: sum(users, "ModuleViews"),
        Completions: sum(users, "ModuleCompletions"),
        __raw: r
      };
    });
  }, [filteredResellers]);

  const canRowExpand = (row) => {
    if (!row || row.Type === "user") {
      return false;
    }

    if (row.Type === "reseller") {
      const rawCompanies = flattenCompanies(row.__raw) || [];
      const companies = hideZero ? rawCompanies.filter(companyHasLogins) : rawCompanies;
      return companies.length > 0;
    }

    if (row.Type === "company") {
      const allUsers = flattenUsers(row.__raw) || [];
      const users = hideZero ? allUsers.filter((u) => (u.Logins || 0) !== 0) : allUsers;
      return users.length > 0;
    }

    return false;
  };

  const toggleRow = (row) => {
    if (row.Type === "reseller") {
      setExpandedResellers((prev) => {
        const next = new Set(prev);
        if (next.has(row.Id)) {
          next.delete(row.Id);
          setExpandedCompanies((prevC) => {
            const nextC = new Set([...prevC].filter((id) => !id.startsWith(`${row.Id}::`)));
            return nextC;
          });
        }
        else {
          next.add(row.Id);
        }
        return next;
      });
    }
    else if (row.Type === "company") {
      setExpandedCompanies((prev) => {
        const next = new Set(prev);
        if (next.has(row.Id)) {
          next.delete(row.Id);
        }
        else {
          next.add(row.Id);
        }
        return next;
      });
    }
  };

  const visibleRows = useMemo(() => {
    return baseResellerRows.flatMap((rRow) => {
      const resellerRow = [rRow];

      if (!expandedResellers.has(rRow.Id)) {
        return resellerRow;
      }

      const rawCompanies = flattenCompanies(rRow.__raw);
      const companies = hideZero
        ? rawCompanies.filter(companyHasLogins) 
        : rawCompanies;
      const companyRows = companies.flatMap((c, companyId) => {
        const allUsers = flattenUsers(c);
        const users = hideZero ? allUsers.filter((u) => (u.Logins || 0) !== 0) : allUsers;

        const cRow = {
          Id: `${rRow.Id}::c-${companyId}`,
          Type: "company",
          Level: 1,
          Name: c.CompanyName,
          Logins: sum(allUsers, "Logins"),
          Views: sum(allUsers, "ModuleViews"),
          Completions: sum(allUsers, "ModuleCompletions"),
          __raw: c,
          __parentResellerId: rRow.Id
        };

        if (!expandedCompanies.has(cRow.Id)) {
          return [cRow];
        }

        const userRows = users.map((u, userId) => ({
          Id: `${cRow.Id}::u-${userId}`,
          Type: "user",
          Level: 2,
          Name: u.Email,
          Email: u.Email,
          Logins: u.Logins,
          Views: u.ModuleViews,
          Completions: u.ModuleCompletions,
          __raw: u
        }));

        return [cRow, ...userRows];
      });

      return [...resellerRow, ...companyRows];
    });
  }, [baseResellerRows, expandedResellers, expandedCompanies, hideZero]);

  const renderNameCell = (row) => {
    const isUser = row.Type === "user";
    const isReseller = row.Type === "reseller";
    const isOpen =
      (row.Type === "reseller" && expandedResellers.has(row.Id)) ||
      (row.Type === "company" && expandedCompanies.has(row.Id));
    const paddingAmount = 16;
    const pad = row.Level * paddingAmount;
    const nameContent = isReseller ? (
      <strong>{row.Name}</strong>
    ) : isUser ? (
      <span className="row-is-user">{row.Name}</span>
    ) : (
      row.Name
    );

    const expandable = canRowExpand(row);

    return (
      <div style={{ display: "flex", gap: 8, paddingLeft: pad, alignItems: "center" }}>
        {!isUser && (
          <span style={{ width: 16, display: "inline-flex", justifyContent: "center" }}>
            {expandable && ( <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} aria-hidden /> )}
          </span>
        )}
        <p style={{ margin: 0 }}>{nameContent}</p>
      </div>
    );
  };

  const grandTotals = useMemo(() => {
    if (!filteredResellers) {
      return { logins: 0, views: 0, completions: 0 };
    }
    const allUsers = filteredResellers.flatMap((r) =>
      flattenCompanies(r).flatMap((c) => flattenUsers(c))
    );
    return {
      logins: sum(allUsers, "Logins"),
      views: sum(allUsers, "ModuleViews"),
      completions: sum(allUsers, "ModuleCompletions")
    };
  }, [filteredResellers]);



  const columns = [
    { field: "Name", headerName: "Reseller/Company/Users", valueGetter: (row) => renderNameCell(row), sortable: false },
    { field: "Logins", headerName: "Logins", style: { width: 90 } },
    { field: "Views", headerName: "Content Views", style: { width: 145 } },
    { field: "Completions", headerName: "Content Completions", style: { width: 190 } }
  ];

  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 100];

  const anyLoading = dataLoading || executingFetch;
  const hasAnyResellers = !!(data && Array.isArray(data.Resellers) && data.Resellers.length > 0);
  const hasAnyUsage = countAllUsers(data?.Resellers) > 0;
  const canDownload = hasAnyUsage && !anyLoading;

  const downloadCsv = () => {
    if (!canDownload) {
      return;
    }

    if (!filteredResellers) {
      return;
    }
    let csv = "ResellerName,CompanyName,Email,Logins,Views,Completions\n";
    filteredResellers.forEach((r) => {
      (r.Companies || []).forEach((c) => {
        (c.Users || []).forEach((u) => {
          csv += [r.ResellerName, c.CompanyName, u.Email, u.Logins, u.ModuleViews, u.ModuleCompletions].join(",") + "\n";
        });
      });
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(
      blob,
        `${packageOptions.find(p => p.value === selectedPackage)?.label}_${formatDateWithHyphen(selectedRange.from, false, true, false)}_to_${formatDateWithHyphen(selectedRange.to, false, true, false)}.csv`
    );
  };

  if (reason !== LogoutReason.None) {
    return (<AuthLogoutMessage reason={reason} />);
  }

  return (
    <div className="usage-report">
      <h1>Usage Report</h1>

      {anyLoading && <LoadingSpinner active />}

      <div className="usage-report-filters">
        <p><strong>Select Package</strong></p>
        <p><strong>Select Date Range</strong></p>
        <p><strong>Download Current View</strong></p>

        <Dropdown
          value={selectedPackage}
          onChange={(e) => handlePackageChange(e.value)}
          options={packageOptions}
          placeholder={
            loadingAvailablePackages
              ? "Loading packages…"
              : packageOptions.length > 0
                ? "Select package"
                : "No packages found"
          }
          disabled={loadingAvailablePackages || !!availPackagesError || packageOptions.length === 0}
        />

				<DatePicker
          monthValue={selectedRange.from}
          onMonthChange={handleMonthChange}
					rangeValue={selectedRange}
					onRangeChange={handleRangeChange}
				/>

        <Button
          type={ButtonType.Primary}
          label="Download Report"
          icon={faDownload}
          onClick={downloadCsv}
          disabled={!canDownload}
        />
      </div>

      <div className="usage-report-nologin">
        <label>
          <input
            type="checkbox"
            checked={hideZero}
            onChange={(e) => setHideZero(e.target.checked)}
          />
          Hide rows with 0 logins
        </label>
      </div>

      {/* Empty/Errors */}
      {!anyLoading && !data && (
        <div className="usage-report-empty">
          {availPackagesError ? (
            <p role="alert">Failed to load packages. Please try again.</p>
          ) : (
            <p>Select a package and month to load the report.</p>
          )}
        </div>
      )}

      {dataError && !anyLoading && (
        <div className="usage-report-error" role="alert">
          <p>{dataError}</p>
        </div>
      )}

      {/* Table & totals */}
      {data && data.Resellers && (
        <>
          {hasAnyResellers ? (
            <>
              <DataTable
                columns={columns}
                rows={visibleRows}
                columnIdName="Id"
                className="usage-report-table"
                onRowClick={(params) => {
                  const row = params.data;
                  if (!canRowExpand(row)) {
                    return;
                  }
                  toggleRow(row);
                }}
                pageSize={pageSize}
                onPageSizeChange={(size) => setPageSize(size)}
                pageSizeOptions={pageSizeOptions}
              />

              <div className="usage-report-totals">
                <p><strong>Totals</strong></p>
                <p className="usage-report-totals-detail"><strong>Logins</strong><br />{grandTotals.logins}</p>
                <p className="usage-report-totals-detail"><strong>Content Views</strong><br />{grandTotals.views}</p>
                <p className="usage-report-totals-detail"><strong>Content Completions</strong><br />{grandTotals.completions}</p>
              </div>
            </>
          ) : (
            <div className="usage-report-empty">
                <p>Sorry, there is no data for this package and date range</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UsageReport;